import { warmDialog } from "../compat/jquery-ui-dialog";
import { isConstrainedNetwork } from "../compat/constrained-network";
/**
 * Non-product page processing — verbatim port of v1 process_nonproduct_page.js
 * (research/01 §3.2; REQ-BOOT-8). bare jQuery = BUNDLED jQuery (webpack
 * ProvidePlugin equivalent). The .then chain is un-caught: config defaults are
 * replaced wholesale (not merged), anchor resolution is sel → #sel → .sel →
 * form[action='/cart/add'] button[type=submit], injection precedence is
 * app block .html / legacy block .html / .append INSIDE the anchor, and the
 * tail runs ProcessConditionalElements(); UpdatePrice(); PreventCheckoutOnEnter().
 */
import jQuery from "jquery";
import { appState } from "../state/app-state";
import { containerDirAttr } from "../i18n/translate";
import { buildCalculator } from "../elements/build-calculator";
import { loadScripts } from "./load-style";
import { applyStoreCss, requestStyles } from "./styles";
import { markCalculatorPage } from "./calculator-page-marker";
import { UpdatePrice, ProcessConditionalElements } from "../pricing";
import { PreventCheckoutOnEnter } from "../common/common-utils";
import { LoadCalculatorNonProduct } from "../api/calculator-app";
import { setTranslations } from "../i18n/translate";
import {
  completeClientPerformanceFailure,
  completeClientPerformanceNoCalculatorAssigned,
  completeClientPerformanceSuccess,
  markClientPerformance,
  markClientPerformanceVisible,
} from "../performance/client-performance-lifecycle";

/**
 * Telemetry pass-throughs for the un-caught render chain below: they report a
 * terminal result and RETHROW the same error, so the silent unhandled
 * rejection that is legacy behavior (BUG-010) is preserved exactly.
 */
function rethrowAsConfigurationRequestFailed(error: any): never {
  completeClientPerformanceFailure("configuration_request_failed", error, undefined, "calculator_app");
  throw error;
}

function rethrowAsRuntimeError(error: any): never {
  completeClientPerformanceFailure("runtime_error", error);
  throw error;
}

export async function ProcessNonProductPage({
  calcUniqueIden,
  elementSelector,
}: any): Promise<void> {
  // Telemetry: the pageurl DTO is in hand, the calculator load starts here.
  markClientPerformance("loader_started");
  // Mirrors ProcessProductPage: the synchronous prologue below (loadScripts,
  // head <link> injection, jQuery DOM work) can throw into an un-awaited
  // promise. Report a safe code and rethrow the SAME error so the silent
  // unhandled rejection (BUG-010) is preserved.
  try {
    var calculatorId: any;

    calculatorId = calcUniqueIden;
    //Fall back to old way if we cant find the calculatorId from the server
    if (calculatorId == null || calculatorId == 0 || calculatorId == undefined) {
      var calculatorElement = document.getElementById("calculator-block");
      var customCalculatorDiv = document.getElementById("custom-calculator");
      calculatorId = jQuery(customCalculatorDiv as any).attr("data-calculatorid");

      if (calculatorId != null && calculatorId != undefined)
        console.log("CalculatorId found from Fallback Method", calculatorId);
      else {
        // The pageurl lookup answered but carried no calculator id, and the
        // DOM fallback has none either: the NOT-APPLICABLE terminal, same as
        // ProcessProductPage. Reported, never counted as a failure.
        // Telemetry-only — the log, the return and the control flow below are
        // unchanged.
        console.log("No calculator found");
        completeClientPerformanceNoCalculatorAssigned();
        return;
      }
    }

    if (calculatorId != null && calculatorId != undefined) {
      appState.cpc_calculatorId = calculatorId;
      console.log("calculator found", calculatorId);

      loadScripts();

      var priceElements = [
        "product-price",
        "product-single__price",
        "selector-wrapper",
        "current_price",
        "product__price",
        "shopify-payment-button",
        "single-option-selector",
      ];
      priceElements.forEach((elem) => {
        var elementToHide = jQuery("." + elem);
        elementToHide.addClass("hide-element");
      });

      //Load css — DIV-035: plugins.css (which now carries the base stylesheet)
      // through the shared loader, dedupes with a boot-time request; the store's
      // own CSS follows from the payload below, exactly as on the product path.
      requestStyles();
      markCalculatorPage(); // DIV-039

      //To hide the loader once page has loaded
      var customCalcBlock = jQuery("#custom-calculator");
      customCalcBlock.hide();

      var calculatorData = LoadCalculatorNonProduct(calculatorId, appState.cpc_isPreview);
      calculatorData
        .then((response) => response.json(), rethrowAsConfigurationRequestFailed)
        .then((json) => {
          // The pageurl DTO only carries the calculator id/selector; the calculator
          // payload itself becomes usable HERE, which is the bundle_ready boundary
          // (mirrors product-page so phase durations are comparable).
          markClientPerformance("bundle_ready");
          appState.cpc_data = json.calculatorData;
          // Mutate the existing array instead of reassigning — other references
          // hold the original array; reassignment would break those stale
          // bindings. (REQ-STATE-1)
          appState.cpc_metaFields.length = 0;
          if (json.metaFields) {
            json.metaFields.forEach((item: any) => appState.cpc_metaFields.push(item));
          }
          let config = {
            smartVariantAutoDeletion: false,
            alertMessageNumberInput:
              "Please enter a value between range {input_range_min} and {input_range_max}",
            thousandSeperator: 0,
            decimalSeperator: 0,
          };
          if (!!json.configuration) config = json.configuration;
          localStorage.setItem("cpc_configData", JSON.stringify(config));
          appState.cpc_configData = config;
          appState.calculatorStyling = json.styling;
          appState.cpc_waitforcartconfirmation = json.waitForCartConfirmation;
          // Translations v1 — see product-page.ts; null for every store without a stored map.
          setTranslations(json.translations?.map);
          applyStoreCss(json.customCss);
          markClientPerformance("configuration_ready");

          console.log("meta-fields", appState.cpc_metaFields);

          var builtData = buildCalculator(appState.cpc_data);

          var elem =
            document.querySelector(elementSelector) ??
            document.querySelector(`#${elementSelector}`) ??
            document.querySelector(`.${elementSelector}`);
          if (elem == null) {
            elem = document.querySelector(
              "form[action='/cart/add'] button[type=submit]",
            );
          }

          console.log("render-block", elem);

          var borderClass =
            appState.calculatorStyling.border === "rounded" ? "class='rounded-edges'" : "";

          // Check if app block container exists (user-chosen placement)
          var appBlockContainer = document.querySelector(".cpc-calculator-target");
          var calculatorBlockContainer = document.getElementById("calculator-block");
          markClientPerformance("mount_target_ready");

          var calculatorHtml = "<div id='calculator' data-calcid='" +
            calculatorId +
            "' " +
            borderClass +
            containerDirAttr() +
            " style='background-color:" +
            appState.calculatorStyling.upperBackground +
            "'>" +
            builtData +
            "</div>";

          if (appBlockContainer) {
            // App block is enabled - render at user chosen place
            console.log("App block detected - rendering at user chosen location");
            jQuery(appBlockContainer as any).html(calculatorHtml);
          } else if (calculatorBlockContainer) {
            // Legacy calculator block - render at user chosen place
            console.log("Calculator block detected - rendering at user chosen location");
            jQuery(calculatorBlockContainer as any).html(calculatorHtml);
          } else {
            // Theme extension or no specific container - render after element (current behavior)
            console.log("Theme extension detected - rendering after element");
            jQuery(elem as any).append(calculatorHtml);
          }
          markClientPerformanceVisible();

          //Custom Store JS
          var shopCustomJsPath =
            appState.baseCalculatorNewApiUrl + "store/script/app?shop=" + appState.cpc_shopUrl;
          if (shopCustomJsPath != null && shopCustomJsPath.length > 0) {
            var customStoreJSRef = document.createElement("script");
            customStoreJSRef.setAttribute("type", "text/javascript");
            customStoreJSRef.setAttribute("src", shopCustomJsPath);
            customStoreJSRef.defer = true;
            customStoreJSRef.setAttribute("crossorigin", "anonymous");
            document
              .getElementsByTagName("head")[0]!
              .appendChild(customStoreJSRef);
          }

          ProcessConditionalElements();
          UpdatePrice();
          PreventCheckoutOnEnter();
          markClientPerformance("calculator_interactive");
          // DIV-031 / DIV-038: dialog warm-up, skipped on a constrained connection.
          if (!isConstrainedNetwork()) warmDialog();
          completeClientPerformanceSuccess({ calculatorId });
        }, rethrowAsConfigurationRequestFailed)
        .then(undefined, rethrowAsRuntimeError);
    }
  } catch (error) {
    completeClientPerformanceFailure("runtime_error", error);
    throw error;
  }
}
